import type { MiddlewareFunction } from 'react-router';
import { ensureAccessToken, getTokenPayload } from '@skynet/data-auth-api';
import type { RootState } from 'src/store';
import { store } from 'src/store';
import { getPrincipal, getPrincipalKey } from '@skynet/authz-provider';
import { fetchAndSetNavigationPermissions } from 'src/utils/cerbos/checkAccessUiNavigation';

import { selectNavigation, selectNavigationPrincipalKey } from 'src/store/navigation/selectors';
import { accessTokenContext, allowedNavActionsContext } from '../context';

/**
 * Root middleware: resolves the access token and the principal's allowed
 * navigation actions, then publishes them to the router context so downstream
 * loaders can read them. Runs before any loader on every navigation.
 *
 * Calls `next()` explicitly to continue the middleware/loader chain.
 */
export const rootMiddleware: MiddlewareFunction = async ({ context }, next) => {
  const state: RootState = store.getState();
  const accessToken = await ensureAccessToken(store, state.authToken?.accessToken);
  context.set(accessTokenContext, accessToken ?? '');
  if (accessToken) {
    const principal = getPrincipal(getTokenPayload(accessToken));
    // Read fresh state after the async ensureAccessToken call.
    const currentState = store.getState();
    const navigationActions = selectNavigation(currentState);
    const cachedPrincipalKey = selectNavigationPrincipalKey(currentState);

    // Cache hit: permissions are present AND belong to the current principal.
    // The principalKey check handles the case where a user logs out without
    // triggering the full signout flow (bypassing resetStore), leaving the
    // previous user's permissions in the store. The key covers id, roles and
    // every principal attribute a policy reads (see `getPrincipalKey`), so
    // switching tenant, role set or granted permissions invalidates the cache.
    if (navigationActions !== null && cachedPrincipalKey === getPrincipalKey(principal)) {
      context.set(allowedNavActionsContext, navigationActions);
      return next();
    }

    // Do not fetch navigation actions authorization data, if already present to avoid unnecessary Authz API calls
    context.set(
      allowedNavActionsContext,
      await fetchAndSetNavigationPermissions(store.dispatch, principal, () => store.getState()),
    );
  }

  return next();
};
